import { Plugin, PluginKey } from '@tiptap/pm/state';
import type { Editor } from '@tiptap/core';
import type { MarkType } from '@tiptap/pm/model';
import type { EditorState } from '@tiptap/pm/state';

// Tiptap's own `linkOnPaste` links anything linkifyjs recognizes, so
// "example.com" pasted over a word links a host the user never spelled out.
const PASTED_URL = /^https?:\/\/\S+$/i;

function isPastedLinkTarget(text: string): boolean {
   return PASTED_URL.test(text.trim());
}

// `link` stacked on `wikiLink` serializes to nested wikitext, so a selection
// holding one takes the normal replace-the-selection paste instead.
function spansWikiLink(state: EditorState, from: number, to: number): boolean {
   let found = false;
   state.doc.nodesBetween(from, to, node => {
      if (node.marks.some(mark => mark.type.name === 'wikiLink')) {
         found = true;
      }
   });
   return found;
}

/**
 * Pasting a web address over selected text links the selection to it instead
 * of replacing the selection with the address, the way Slack does.
 */
export function linkSelectionOnPaste({
   editor,
   type,
   target,
}: {
   editor: Editor;
   type: MarkType;
   target: string;
}): Plugin {
   return new Plugin({
      key: new PluginKey('linkSelectionOnPaste'),
      props: {
         handlePaste: (view, _event, slice) => {
            const { selection } = view.state;
            if (selection.empty) {
               return false;
            }

            // Plain text arrives one paragraph per line and a line break is a
            // leaf node, so a newline in both places keeps a multi-line paste
            // from reading as one address.
            const pasted = slice.content.textBetween(0, slice.content.size, '\n', '\n');
            if (!isPastedLinkTarget(pasted)) {
               return false;
            }
            if (spansWikiLink(view.state, selection.from, selection.to)) {
               return false;
            }

            // addMark re-points a link already inside the selection.
            const linked = editor.commands.setMark(type, { href: pasted.trim(), target });
            if (linked) {
               // The selection doesn't move, so nothing else tells the bubble
               // its mark changed.
               editor.commands.showLinkEditor('preview');
            }
            return linked;
         },
      },
   });
}
